import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const TermsConditions = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Commonwealth FC</title>
        <meta name="description" content="Commonwealth FC terms and conditions including SMS messaging program details." />
      </Helmet>
      <div className="min-h-screen bg-[#0C2E3C]">
        {/* Header */}
        <div className="bg-[#0C2E3C] border-b border-[#F0F4F3]/10">
          <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
            <Link to="/" className="text-[#C8E64E] font-heading text-2xl tracking-wider hover:text-[#C8E64E]/80 transition-colors" data-testid="terms-home-link">
              COMMONWEALTH FC
            </Link>
            <Link to="/privacy" className="text-sm text-[#F0F4F3]/50 hover:text-[#C8E64E] transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
          <h1 className="font-heading text-4xl text-[#C8E64E] mb-2">Terms & Conditions</h1>
          <p className="text-[#F0F4F3]/40 text-sm mb-10">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

          <div className="prose-custom space-y-8">
            {/* Program Info */}
            <section>
              <h2>SMS Messaging Program</h2>
              <div className="info-card">
                <p><strong>Program Name:</strong> Commonwealth FC Notifications</p>
                <p><strong>Description:</strong> By opting in to Commonwealth FC text messages, you consent to receive automated SMS notifications related to your registration, tryout scheduling, roster offers, follow-up reminders, and club announcements from Commonwealth FC.</p>
                <p><strong>Message Frequency:</strong> Message frequency varies. You may receive up to 10 messages per month depending on your registration status, upcoming tryouts, and club communications.</p>
                <p><strong>Message & Data Rates:</strong> Message and data rates may apply. Please contact your wireless carrier for details about your messaging plan.</p>
              </div>
            </section>

            {/* Opt-out */}
            <section>
              <h2>Opt-Out Instructions</h2>
              <p>You can opt out of receiving text messages at any time.</p>
              <div className="info-card">
                <p>To stop receiving messages, text <span className="keyword">STOP</span> to our number. You will receive a one-time confirmation message and will no longer receive SMS from Commonwealth FC.</p>
                <p>For help or more information, text <span className="keyword">HELP</span> to our number, or contact us using the information below.</p>
              </div>
            </section>

            {/* Support Contact */}
            <section>
              <h2>Support Contact Information</h2>
              <div className="info-card">
                <p><strong>Email:</strong> info@commonwealthfc.com</p>
                <p><strong>Phone:</strong> (434) 722-3226</p>
                <p><strong>Website:</strong> <Link to="/" className="text-[#1FE3A8] hover:underline">commonwealthfc.com</Link></p>
              </div>
            </section>

            {/* General Terms */}
            <section>
              <h2>General Terms of Use</h2>
              <p>By accessing and using the Commonwealth FC website and services, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.</p>
            </section>

            <section>
              <h2>Eligibility</h2>
              <p>Our programs are intended for youth soccer players and their parents or legal guardians. By submitting a registration or interest form, you represent that you are a parent or legal guardian of the registered player, or that you are 18 years of age or older.</p>
            </section>

            <section>
              <h2>Registration & Tryouts</h2>
              <p>Submitting an interest form does not guarantee a roster spot. All registrations are subject to review and tryout availability. Commonwealth FC reserves the right to accept or decline any registration at its sole discretion.</p>
              <p>Tryout schedules, locations, and roster offers are communicated via email and SMS to the contact information you provide. It is your responsibility to ensure your contact information is accurate and up to date.</p>
            </section>

            <section>
              <h2>Communications</h2>
              <p>By providing your email address and phone number, you consent to receive communications from Commonwealth FC including but not limited to:</p>
              <ul>
                <li>Registration confirmations</li>
                <li>Tryout invitations and reminders</li>
                <li>Roster offer notifications</li>
                <li>Club announcements and newsletters</li>
                <li>Follow-up messages from coaching staff</li>
              </ul>
              <p>You may opt out of email communications at any time by using the unsubscribe link in any email, or opt out of SMS by texting <span className="keyword">STOP</span>.</p>
            </section>

            <section>
              <h2>Intellectual Property</h2>
              <p>All content on this website — including logos, text, images, and design — is the property of Commonwealth FC and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without prior written consent.</p>
            </section>

            <section>
              <h2>Limitation of Liability</h2>
              <p>Commonwealth FC shall not be liable for any injuries sustained during tryouts, practices, or games. Participation in all soccer activities is at your own risk. Parents and guardians are responsible for ensuring their child is physically fit to participate.</p>
            </section>

            <section>
              <h2>Changes to Terms</h2>
              <p>Commonwealth FC reserves the right to modify these Terms & Conditions at any time. Changes will be effective immediately upon posting to this page. Your continued use of our services constitutes acceptance of any changes.</p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#F0F4F3]/10 py-6 text-center text-[#F0F4F3]/30 text-sm">
          <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>&copy; {new Date().getFullYear()} Commonwealth FC</span>
            <Link to="/privacy" className="text-[#F0F4F3]/40 hover:text-[#C8E64E] transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>

      <style>{`
        .prose-custom h2 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #F0F4F3;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(240, 244, 243, 0.08);
        }
        .prose-custom p {
          color: rgba(240, 244, 243, 0.65);
          line-height: 1.75;
          margin-bottom: 0.5rem;
        }
        .prose-custom ul {
          color: rgba(240, 244, 243, 0.65);
          padding-left: 1.5rem;
          margin: 0.75rem 0;
        }
        .prose-custom li {
          margin-bottom: 0.35rem;
          list-style-type: disc;
        }
        .prose-custom strong {
          color: #F0F4F3;
        }
        .prose-custom .info-card {
          background: rgba(240, 244, 243, 0.04);
          border: 1px solid rgba(240, 244, 243, 0.08);
          border-radius: 4px;
          padding: 1.25rem;
        }
        .prose-custom .info-card p {
          margin-bottom: 0.4rem;
        }
        .prose-custom .keyword {
          display: inline-block;
          background: rgba(200, 230, 78, 0.15);
          color: #C8E64E;
          font-weight: 700;
          padding: 0.1rem 0.5rem;
          border-radius: 3px;
          font-size: 0.95rem;
          letter-spacing: 0.05em;
        }
      `}</style>
    </>
  );
};

export default TermsConditions;
